import { acceptHMRUpdate, defineStore } from 'pinia'
import { throttle, clone } from 'lodash-es'

import { useSettingsGeneralStore } from './settings-general'

import { type CurrenciesValues, useValues } from '@/composables/values'
import DuplicateConfirmationError, {
  isDuplicateConfirmationError,
} from '@/errors/DuplicateConfirmationError'
import { HttpError } from '@/helpers/errors'
import AccountService from '@/services/account/account'
import AssetsService from '@/services/account/assets'
import { dateToUTCString } from '@/services/date.service'

import type {
  AssetSourcesAssetChild,
  AssetsTableParams,
  BlockchainData,
  BlockchainRecord,
  BlockchainsTableParams,
  Claimable,
  UpdateBlockchainData,
} from '@/types/services/assets'
import type {
  BlockchainBalance,
  CheckBlockchainData,
} from '@/types/services/account'
import type { TagModelValue } from '@/types/components/tags'

export interface BlockchainsState {
  table: {
    data: BlockchainRecord[]
    loading: boolean
  }
  assets: {
    netValues: Map<string, CurrenciesValues>
    data: Map<string, AssetSourcesAssetChild[]>
    loading: boolean
  }
  detailAssets: {
    data: Map<string, AssetSourcesAssetChild[]>
    loading: boolean
  }
  claimables: {
    data: Claimable[]
    loading: boolean
  }
}

let assetsAbortController: AbortController | null = null
let cancelSetDataThrottled: (() => void) | null = null

export const useAssetsBlockchainsStore = defineStore('assetsBlockchains', {
  state(): BlockchainsState {
    return {
      table: {
        data: [],
        loading: true, // True by default to show preloading
      },
      assets: {
        netValues: new Map(),
        data: new Map(),
        loading: false,
      },
      detailAssets: {
        data: new Map(),
        loading: false,
      },
      claimables: {
        data: [],
        loading: false,
      },
    }
  },

  actions: {
    async getTableData(params: BlockchainsTableParams) {
      this.$patch((state) => {
        state.table.data = []
        state.table.loading = true
      })

      try {
        const response = await AssetsService.getBlockchainsData(params)

        this.$patch((state) => {
          state.table.data = response.data.data
          state.table.loading = false
        })
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async getAssets(
      params: Omit<AssetsTableParams, 'return_type'>,
      forDetail = false
    ) {
      if (cancelSetDataThrottled) {
        cancelSetDataThrottled()
        cancelSetDataThrottled = null
      }
      if (assetsAbortController) {
        assetsAbortController.abort()
      }
      assetsAbortController = new AbortController()

      const targetKey = forDetail ? 'detailAssets' : 'assets'

      this.$patch((state) => {
        if (!forDetail) {
          state.assets.netValues = new Map()
        }
        state[targetKey].data = new Map()
        state[targetKey].loading = true
      })

      let cachedChildren: AssetSourcesAssetChild[] = []
      const { findCurrenciesValues } = useValues()

      const setData = () => {
        const data = clone(this[targetKey].data)
        const netValues = clone(this.assets.netValues)

        cachedChildren.forEach((child) => {
          if (child.account_type !== 'blockchain') {
            return
          }

          const key = child.account_id
          if (data.has(key)) {
            data.get(key)?.push(child)
          } else {
            data.set(key, [child])
          }

          if (!forDetail) {
            const currentNetValues = netValues.get(key)
            const childNetValues = findCurrenciesValues(child.target_net_values)
            netValues.set(key, {
              fiat: (currentNetValues?.fiat ?? 0) + (childNetValues?.fiat ?? 0),
              crypto:
                (currentNetValues?.crypto ?? 0) + (childNetValues?.crypto ?? 0),
            })
          }
        })

        this.$patch((state) => {
          state[targetKey].data = data

          if (!forDetail) {
            state.assets.netValues = netValues
          }
        })
        cachedChildren = []
      }

      const setDataThrottled = throttle(setData, 500)
      cancelSetDataThrottled = setDataThrottled.cancel

      const eventHandlers = {
        assets_with_price: (data: AssetSourcesAssetChild) => {
          cachedChildren.push(data)
        },
      }

      try {
        await AssetsService.getAssetsStream(
          {
            ...params,
            return_type: 'asset_sources',
          },
          ({ event, data }) => {
            eventHandlers[event]?.(data)
            setDataThrottled()
          },
          () => {
            setDataThrottled.cancel()
            setData()
            this[targetKey].loading = false
          },
          assetsAbortController
        )
      } catch (err) {
        this[targetKey].loading = false
        if (err instanceof DOMException && err.name === 'AbortError') {
          return
        }
        throw new HttpError(err)
      }
    },

    async createEntry({
      name,
      entity_id,
      entry_data,
      tags,
      note,
      balances,
      force,
    }: {
      name: string
      entity_id: string
      entry_data: BlockchainData
      tags: TagModelValue[]
      note: string | null
      balances: BlockchainBalance[]
      force?: boolean
    }) {
      const settingsGeneralStore = useSettingsGeneralStore()

      try {
        const date = new Date()
        const createDate = dateToUTCString(date)

        const response = await AccountService.createAccountAndEntry(
          {
            entity_id,
            name,
            create_date: createDate,
            note,
            type: 'blockchain',
          },
          { ...entry_data, force },
          balances
        )

        await settingsGeneralStore.assignAccountTags({
          accountId: response.data.data.account.id,
          tags,
        })
      } catch (err) {
        if (isDuplicateConfirmationError(err)) {
          throw new DuplicateConfirmationError(
            err.response.data.data.duplicates,
            err.response.data.result?.message || ''
          )
        }
        throw new HttpError(err)
      }
    },

    async updateEntry({
      entry_id,
      account_id,
      entry_data,
      tags,
      entity_id,
      name,
      note,
      deactivate_date,
    }: {
      entry_id: string
      account_id: string
      entry_data: UpdateBlockchainData
      tags: TagModelValue[]
      entity_id: string
      name: string
      note: string | null
      deactivate_date?: string
    }) {
      const settingsGeneralStore = useSettingsGeneralStore()

      try {
        await settingsGeneralStore.assignAccountTags({
          accountId: account_id,
          tags,
        })

        await AssetsService.updateEntry(
          account_id,
          entry_id,
          'blockchain',
          entry_data
        )

        await AccountService.updateAccount(account_id, {
          entity_id,
          deactivate_date: deactivate_date,
          name,
          note,
        })
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async deleteEntry(account_id: string) {
      try {
        await AccountService.deleteAccount(account_id)
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async deactivateEntry(
      account_id: string,
      entity_id: string,
      deactivate_date: string
    ) {
      try {
        await AccountService.updateAccount(account_id, {
          entity_id,
          deactivate_date,
        })
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async checkEntry(params: CheckBlockchainData) {
      try {
        const response = await AccountService.checkBlockchain(params)

        const result = response.data.result
        if (!result.success) {
          return {
            success: false,
            message: result.message,
          }
        }

        const data = response.data.data
        return {
          success: data.success,
          balances: data.balances,
          message: data.error,
        }
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async getClaimables(entity_id: string) {
      this.$patch((state) => {
        state.claimables.data = []
        state.claimables.loading = true
      })

      try {
        const response = await AssetsService.getClaimables(entity_id)

        this.$patch((state) => {
          state.claimables.data = response.data.data
          state.claimables.loading = false
        })
      } catch (err) {
        throw new HttpError(err)
      }
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useAssetsBlockchainsStore, import.meta.hot)
  )
}
